// RoomSettingsModal — Phase 6 Task 6.6-B 入室後の設定モーダル.
//
// ClassicLayout のヘッダ「設定」ボタンから開く. Modal 内のフィールドは
// **各フィールド独立保存** で 1 回の API 呼び出しに対応 (一括 submit ではない).
//
// Fields:
//   1. ルーム名             → PATCH /admin/rooms/:id { name }
//   2. オーナーパスワード   → POST /admin/rooms/:id/owner-password { password }
//   2.5. 入室あいことば     → PATCH /admin/rooms/:id { entryPassword } (string=設定/変更、
//                              null=解除。owner-vs-entry-password-2026-06-13.md。
//                              オーナーパスワードとは別セクション — 入室するゲスト用の鍵で
//                              あなた(オーナー)は打たない、という区別を見出し/説明文で出す)
//   3. email 登録           → POST /admin/rooms/:id/email { email }
//   4. 公開フラグ (CB)      → PATCH /admin/rooms/:id { isPublic } (CB toggle で即保存)
//   5. clearOpen (CB)       → PATCH /admin/rooms/:id { clearOpen } (Phase 7 Task 7-5、
//                              CB toggle で即保存)
//
// 認証: AdminTokenStore.get(roomId) で localStorage から取って Authorization
// Bearer に載せる. **adminToken が無い場合はモーダル自体を出さない** ように
// 親 (ClassicLayout) で gate するが、防御として本コンポーネントでも null
// チェックして「ログインしてください」案内を出す.
//
// canvas size 変更不可: Phase 10 で動的リサイズ実装まで変更不可 (確定方針 I).
// 設定モーダルでは項目を出さず注記のみ.
//
// ルーム削除: Task 8 (管理画面) でカバー、ここでは出さない.
//
// Accessibility: `role="dialog"` + `aria-modal="true"` + Esc で閉じる. focus
// trap は React 19 の <dialog> element + showModal API でも実装可能だが、
// 設定モーダル程度で十分なベースラインとして role+aria のみ + Esc キー
// ハンドラで対応.

import { MAX_ENTRY_PASSWORD_LENGTH, normalizeEntryPassword } from '@oekaki/protocol';
import { useCallback, useEffect, useId, useState } from 'react';

import {
  CREDENTIAL_DISPLAY_NAME_MAX_LENGTH,
  CREDENTIAL_LOGIN_ID_MAX_LENGTH,
  CREDENTIAL_PASSWORD_MAX_LENGTH,
  CREDENTIAL_PASSWORD_MIN_LENGTH,
  credentialDisplayNameErrorText,
  credentialLoginIdErrorText,
  credentialPasswordErrorText,
  credentialPermissionsErrorText,
  mapCreateCredentialOutcome,
  mapDeleteCredentialOutcome,
  mapListCredentialsOutcome,
  shouldNavigateToLogin,
  validateCredentialForm,
} from '../application/adminPage.js';
import {
  ENTRY_SHOW_MEMBERS_FIELD,
  ENTRY_SHOW_PREVIEW_FIELD,
  type EntryVisibilityFieldSpec,
  entryFlagInitial,
} from '../application/entryVisibilityFields.js';
import {
  type FieldSaveResult,
  mapPatchRoomOutcome,
  mapSetOwnerEmailOutcome,
  mapSetOwnerPasswordOutcome,
  OWNER_EMAIL_MAX_LENGTH,
  OWNER_PASSWORD_MAX_LENGTH,
  OWNER_PASSWORD_MIN_LENGTH,
  ownerEmailErrorText,
  ownerPasswordErrorText,
  ROOM_NAME_MAX_LENGTH,
  roomNameErrorText,
  validateOwnerEmail,
  validateOwnerPassword,
  validateRoomName,
} from '../application/roomSettingsForm.js';
import {
  permissionsBitflagFromBooleans,
  permissionsBooleansFromBitflag,
} from '../application/uiFeedback.js';
import type {
  AdminAuthOptions,
  CredentialSummary,
  DispatcherClient,
  RoomAdminAuthOptions,
} from '../infrastructure/dispatcher/DispatcherClient.js';
import { readEnv } from '../infrastructure/env.js';
import {
  type FetchRoomRetentionInput,
  type FetchRoomRetentionOutcome,
  fetchRoomRetention,
  type RetentionTier,
  type RoomRetention,
} from '../infrastructure/retention/retentionClient.js';
import { AlphaDataWarningNotice } from './AlphaWarningBanner.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { EntryVisibilityCheckbox } from './EntryVisibilityCheckbox.js';
import { useSession } from './hooks/useSession.js';
import { navigateToView } from './urlNavigation.js';

export interface RoomSettingsModalProps {
  readonly roomId: string;
  /** 表示中のルーム名 (初期値). 保存後は親が伝播してくれない場合もあるので独立 state を持つ. */
  readonly roomName: string;
  /** 公開フラグ初期値. 親が把握していなければ `false`. */
  readonly isPublic?: boolean;
  /**
   * Phase 7 Task 7-5: clearOpen 初期値. 親が把握していなければ `false`.
   * `true` なら誰でも `whiteboard.clear` を実行可能 (旧版風オープン運用).
   * `false` なら `Perm.CLEAR` 保有 credential のみ clear 可能 (default).
   */
  readonly clearOpen?: boolean;
  /**
   * entry-preview-members-2026-07-21: 入室画面 snapshot プレビュー表示の
   * **既知の現在値** (boolean). AdminPage は `AdminRoomSummary.settings` の値を
   * `entryFlagInitial` (undefined = On) で解決してから渡す.
   *
   * **undefined = 現在値不明** (入室中 layout 経由 — room.welcome に載らない).
   * その場合は modal open 時に `lookupRoomServer` を 1 回叩いて
   * `roomMeta.settings` から現在値を取得する (案 A 同梱の staleness 解消).
   * lookup 失敗は fail-silent に On フォールバック.
   */
  readonly entryShowPreview?: boolean | undefined;
  /** entry-preview-members-2026-07-21: 入室画面参加者リスト表示の既知の現在値. undefined = 不明 → open 時 lookup. */
  readonly entryShowMembers?: boolean | undefined;
  /**
   * Phase 6 Task 8: 管理画面 (`/admin`) からの起動時に true. 認証アカウント
   * (credential) 管理タブを追加表示する. ClassicLayout のヘッダ「設定」からは
   * false (= Task 6.6-B 既存挙動).
   */
  readonly showCredentials?: boolean;
  /** Modal を閉じる. */
  readonly onClose: () => void;
  /**
   * テスト注入: retention (保存期間) 取得。既定は実 fetch。RoomRetentionInfo
   * (owner-authed な保存期間はしご) の mount 時 1 回取得に使う。
   */
  readonly loadRetention?: (input: FetchRoomRetentionInput) => Promise<FetchRoomRetentionOutcome>;
}

const isResultError = (r: FieldSaveResult): boolean =>
  r.outcome === 'unauthorized' ||
  r.outcome === 'invalid_body' ||
  r.outcome === 'rate_limited' ||
  r.outcome === 'unknown';

const resultToneClass = (r: FieldSaveResult): string => {
  if (r.outcome === 'ok') return 'text-[var(--color-success)]';
  if (r.outcome === 'partial') return 'text-[var(--color-accent-soft)]';
  return 'text-[var(--color-error)]';
};

interface UseAdminAuthArgs {
  readonly roomId: string;
}

/**
 * Read the admin token for the given room from the SessionProvider's
 * AdminTokenStore. Returns `null` if absent. (We re-read on every render
 * because the token can be set/cleared by other components — Task 7 login
 * flow / Task 8 admin page.)
 */
const useAdminToken = ({ roomId }: UseAdminAuthArgs): string | null => {
  const { adminTokens } = useSession();
  // Resolved on every render — cheap localStorage read. State is not required
  // because re-renders are driven by the modal's local state changes.
  return adminTokens.get(roomId);
};

export const RoomSettingsModal = ({
  roomId,
  roomName,
  // 2026-05-23: PublicToggle 非表示化 (= W2 公開時点では使わない、Phase 14+ で
  // 復活). prop は API 互換性のため残すが現状未使用、`_` prefix で意図明示.
  isPublic: _isPublic = false,
  clearOpen = false,
  entryShowPreview,
  entryShowMembers,
  showCredentials = false,
  onClose,
  loadRetention,
}: RoomSettingsModalProps): React.ReactNode => {
  const { dispatcher } = useSession();
  const adminToken = useAdminToken({ roomId });

  // Esc to close.
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [onClose]);

  // entry-preview-members-2026-07-21 案 A 同梱 (staleness 解消): 入室中
  // (layout 経由) は entry 2 フラグの現在値ソースが無い (props undefined) ので、
  // open 時に lookupRoomServer を 1 回叩いて roomMeta.settings から現在値を
  // 取得する. AdminPage 経由は実値 (boolean) が渡ってくるので entry フラグ側は
  // fetch 結果を使わない.
  //
  // 入室「あいことば」(owner-vs-entry-password-2026-06-13.md、entry-password
  // 実装スライス) の `entryPasswordRequired` は entry フラグと違い props 経由で
  // 「既知の現在値」を受け取る仕組みが無い (AdminPage 側も未対応) ため、
  // **常に** この lookup 結果から取得する — entryFlagsKnown (AdminPage 経由)
  // でも省略しない. 2 つの効果 (entry フラグ / entryPasswordRequired) を
  // 同じ 1 回の HTTP 呼び出しにまとめている (別々の useEffect にすると
  // モーダル open のたび lookupRoomServer が 2 回飛んでしまう).
  //
  // lookup 失敗は両方 fail-silent フォールバック: entry フラグは On
  // (undefined = On のセマンティクス維持)、entryPasswordRequired は「未設定」
  // 側 (= 誤って「設定済」バッジを出さない安全な既定) に倒す。
  const entryFlagsKnown = entryShowPreview !== undefined && entryShowMembers !== undefined;
  const [fetchedEntryFlags, setFetchedEntryFlags] = useState<{
    readonly preview: boolean;
    readonly members: boolean;
  } | null>(null);
  const [entryPasswordRequired, setEntryPasswordRequired] = useState<boolean | null>(null);
  useEffect(() => {
    // adminToken 無し (かつ非 AdminPage) は UnauthenticatedNotice になり
    // フィールド自体を出さないので lookup も省く.
    if (adminToken === null) return;
    let cancelled = false;
    void (async () => {
      const out = await dispatcher.lookupRoomServer(roomId);
      if (cancelled) return;
      if (!entryFlagsKnown) {
        if (out.outcome === 'ok') {
          setFetchedEntryFlags({
            preview: entryFlagInitial(out.roomMeta.settings.entryShowPreview),
            members: entryFlagInitial(out.roomMeta.settings.entryShowMembers),
          });
        } else {
          setFetchedEntryFlags({ preview: true, members: true });
        }
      }
      setEntryPasswordRequired(out.outcome === 'ok' ? out.roomMeta.entryPasswordRequired : false);
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatcher, roomId, entryFlagsKnown, adminToken]);

  // props (既知の現在値) 優先、無ければ open 時 lookup の解決値. null = 解決待ち.
  const resolvedEntryFlags =
    entryShowPreview !== undefined && entryShowMembers !== undefined
      ? { preview: entryShowPreview, members: entryShowMembers }
      : fetchedEntryFlags;

  // Phase 6 Task 8: AdminPage 経由 (showCredentials=true) は cookie session で
  // 認証されている前提なので adminToken 無くても動かせる. ClassicLayout 経由
  // (showCredentials=false) は従来通り admin_token gate.
  if (adminToken === null && !showCredentials) {
    return <UnauthenticatedNotice onClose={onClose} />;
  }

  // Phase 6 Task 8: cookie session のみ経路 (adminToken null) も Bearer-or-cookie
  // 両対応 endpoint を呼ぶので、AdminAuthOptions のみが必要な既存 field 経由は
  // dummy bearer ('') を渡したくない. そのため field の auth は adminToken が
  // 無いときは disable する形に倒す (= showCredentials=true で adminToken も
  // 同時に持っているケースのみ既存 field を出す). 大半のケースは
  // verifyMagicLink 成功 → AdminPage 着地時に adminToken 1 個分は手元にある.
  const auth: AdminAuthOptions | null = adminToken !== null ? { adminToken } : null;
  // 2026-05-23: CredentialsPanel 非表示化で未使用に. Phase 14+ で復活時に
  // CredentialsPanel コメント解除 + 本変数の `_` prefix 除去.
  const _credAuth: RoomAdminAuthOptions = adminToken !== null ? { adminToken } : {};

  return (
    <ModalShell onClose={onClose}>
      {/* α 期データ削除警告 (2026-05-23 W2 公開向け). モーダル冒頭に置いて
          各 field の独立保存ボタン全体に効く位置にする. */}
      <div className="mb-3">
        <AlphaDataWarningNotice />
      </div>
      {auth !== null ? (
        <>
          <NameField roomId={roomId} initial={roomName} dispatcher={dispatcher} auth={auth} />
          <hr className="my-4 border-white/10" />
          {/* この部屋の保存期間 (retention はしご). owner-authed (auth.adminToken)
              な modal でだけ取得・表示する = P1 チップのタップ先の「詳細の正本」.
              取得失敗時は黙って非表示 (RoomRetentionInfo が null を返す). */}
          <RoomRetentionInfo
            roomId={roomId}
            adminToken={auth.adminToken}
            {...(loadRetention !== undefined ? { loadRetention } : {})}
          />
          <PasswordField roomId={roomId} dispatcher={dispatcher} auth={auth} />
          <hr className="my-4 border-white/10" />
          {/* 入室「あいことば」(owner-vs-entry-password-2026-06-13.md): オーナー
              パスワード (上のセクション) とは別セクション・別見出しで分ける
              (「画面を分ける」確定方針の modal 内版 — 同一画面ではあるが
              見出し・説明文で使う人を分けて混同を防ぐ). */}
          <EntryPasswordField
            roomId={roomId}
            dispatcher={dispatcher}
            auth={auth}
            current={entryPasswordRequired}
          />
          <hr className="my-4 border-white/10" />
          <EmailField roomId={roomId} dispatcher={dispatcher} auth={auth} />
          <hr className="my-4 border-white/10" />
          {/* 「ルームを公開する」 toggle は W2 公開時点では非表示 (= Phase 14+
              HomePage list の運用がまだ無く、誤操作で公開状態になる事故を防ぐ).
              将来の公開 list 機能投入時に復活させる. server / DB の isPublic
              フィールド自体は残しているので、復活は本行のコメント解除のみ. */}
          {/* <PublicToggle roomId={roomId} initial={isPublic} dispatcher={dispatcher} auth={auth} /> */}
          {/* <hr className="my-4 border-white/10" /> */}
          <ClearOpenToggle
            roomId={roomId}
            initial={clearOpen}
            dispatcher={dispatcher}
            auth={auth}
          />
          <hr className="my-4 border-white/10" />
          {/* entry-preview-members-2026-07-21: 入室画面の表示 2 トグル (文言は
              共有 spec 由来 = CreateRoomPage と同一知識). 現在値が未解決の間は
              トグルを出さない (解決後 mount = useState(initial) が正しい値で
              初期化される). */}
          {resolvedEntryFlags !== null ? (
            <>
              <EntryVisibilityToggle
                roomId={roomId}
                spec={ENTRY_SHOW_PREVIEW_FIELD}
                initial={resolvedEntryFlags.preview}
                dispatcher={dispatcher}
                auth={auth}
              />
              <hr className="my-4 border-white/10" />
              <EntryVisibilityToggle
                roomId={roomId}
                spec={ENTRY_SHOW_MEMBERS_FIELD}
                initial={resolvedEntryFlags.members}
                dispatcher={dispatcher}
                auth={auth}
              />
            </>
          ) : (
            <p className="text-xs text-[var(--color-text-muted)]" role="status">
              入室画面の表示設定を読み込み中…
            </p>
          )}
          <hr className="my-4 border-white/10" />
          <CanvasSizeNotice />
        </>
      ) : (
        <p className="mb-3 rounded border border-white/10 bg-[var(--color-bg-soft)] px-2 py-2 text-xs text-[var(--color-text-muted)]">
          この端末にはこのルームのオーナー権限がありません。
          <br />
          ルーム名・オーナーパスワード・公開設定の変更にはオーナーとしての認証が必要です。
          <br />
          ログイン画面からオーナーパスワードまたはマジックリンクで認証してください。
        </p>
      )}
      {/* 2026-05-23: 認証アカウント (credential) 発行 UI は W2 公開時点では
          非表示 (= AdminPage 経由でしか触れない + UI 配置を再検討したい、
          ユーザー判断). showCredentials prop / CredentialsPanel 実装は丸ごと
          残してあるので、Phase 14+ で UI 整理した上で復活させる. */}
      {/* {showCredentials && (
        <>
          <hr className="my-4 border-white/10" />
          <CredentialsPanel roomId={roomId} dispatcher={dispatcher} auth={credAuth} />
        </>
      )} */}
    </ModalShell>
  );
};

/* ─── shell ──────────────────────────────────────────────────────────────── */

const ModalShell = ({
  children,
  onClose,
}: {
  readonly children: React.ReactNode;
  readonly onClose: () => void;
}): React.ReactNode => {
  const titleId = useId();
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      {/* Backdrop as a real button so a11y stays compliant (Esc is also wired
          via window keydown in the parent). */}
      <button
        type="button"
        aria-label="閉じる"
        tabIndex={-1}
        onClick={onClose}
        className="-z-10 absolute inset-0 cursor-default bg-black/60"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        // w-full max-w + min-w-0: 固定 w-[520px] はスマホ縦 (390/360px) で親 p-4 を
        // 大きく貫通してはみ出す (2026-07-03 一括点検)。min-w-0 は credential token 等の
        // 長い無改行文字列が intrinsic min で max-w を貫通するのを防ぐ保険。
        className="max-h-[90vh] w-full min-w-0 max-w-[520px] overflow-y-auto rounded border border-white/10 bg-[var(--color-panel)] p-6 shadow-xl"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 id={titleId} className="text-lg font-bold text-[var(--color-accent)]">
            ルーム設定
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            className="rounded px-2 py-1 text-base text-[var(--color-text-muted)] hover:bg-white/10"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

/* ─── unauth notice (admin_token なし) ───────────────────────────────────── */

/**
 * Phase 6 Task 7: Navigate to `/login` for cross-room login.
 *
 * Uses `pushState` + popstate (HomePage / LoginPage と同じパターン) so the
 * back button can return the user to the previous (room) view. We close the
 * modal first so the route switch happens against a clean root.
 */
const goToLoginScreen = (): void => {
  navigateToView('login');
};

const UnauthenticatedNotice = ({ onClose }: { readonly onClose: () => void }): React.ReactNode => (
  <ModalShell onClose={onClose}>
    <p className="mb-4 text-sm text-[var(--color-text-muted)]">
      この端末にはこのルームのオーナー権限が見つかりません。
      <br />
      別の端末やブラウザで作成したルームの設定を変更するには、ログイン画面からオーナーパスワード
      または マジックリンクで認証してください。
    </p>
    <div className="flex gap-2">
      <button
        type="button"
        onClick={onClose}
        className="flex-1 rounded border border-white/30 bg-[var(--color-bg-soft)] px-4 py-2 text-base text-white hover:bg-[var(--color-bg)]"
      >
        閉じる
      </button>
      <button
        type="button"
        onClick={() => {
          onClose();
          goToLoginScreen();
        }}
        className="flex-1 rounded bg-[var(--color-accent)] px-4 py-2 text-base font-bold text-[var(--color-panel)] transition hover:brightness-110"
      >
        ログイン画面へ
      </button>
    </div>
  </ModalShell>
);

/* ─── shared field shell ─────────────────────────────────────────────────── */

const FieldFeedback = ({
  result,
}: {
  readonly result: FieldSaveResult | null;
}): React.ReactNode => {
  if (result === null) return null;
  const toneCls = resultToneClass(result);
  // 'ok' without explicit message just means saved silently — show a generic line.
  const message =
    result.outcome === 'ok' && result.message === undefined ? '保存しました。' : result.message;
  return (
    <div className="mt-1">
      <p className={`text-xs ${toneCls}`} role={isResultError(result) ? 'alert' : 'status'}>
        {message}
      </p>
      {/* Phase 6 Task 7: 401 unauthorized は localStorage の adminToken が
          失効している (room.owner_email 変更 / DB rotate / cookie expired 等).
          ログイン画面に誘導する inline CTA を出して、ユーザがすぐ再認証
          できるようにする. */}
      {result.outcome === 'unauthorized' && (
        <button
          type="button"
          onClick={goToLoginScreen}
          className="mt-1 text-xs text-[var(--color-accent-soft)] underline transition hover:brightness-110"
        >
          ログイン画面へ
        </button>
      )}
    </div>
  );
};

/* ─── retention 説明 (保存期間はしご) ───────────────────────────────────── */
//
// retention-communication-2026-07-14.md A1. P1 チップ (RoomRetentionChip) の
// タップ先 = この modal は「詳細の正本」なので、全 tier のはしごを出して現在の
// tier をハイライトし、削除条件を明示する.
//
// - owner-authed (auth.adminToken 保有) なときだけ mount 時に 1 回 retention を
//   取得する. cookie session のみ経路 (adminToken null) は auth === null で
//   フィールドごと非表示なので、ここも呼ばれない (bearer が無く endpoint を
//   叩けないため既存 gate に自然に従う).
// - **数値は server の policy を埋め込む** (7/30 を直書きしない = 陳腐化防止).
// - 取得失敗 (unauthorized / not-found / error) は黙って非表示 (null を返す).
//   設定操作自体は従来通り可能.

/** はしごの行 (全 tier を上から並べる). tier は server `RetentionTier` と一致. */
const RETENTION_LADDER: readonly { readonly tier: RetentionTier; readonly title: string }[] = [
  { tier: 'anonymous', title: '匿名のまま' },
  { tier: 'password', title: 'オーナーパスワード登録' },
  { tier: 'email', title: 'メールアドレス登録' },
];

/**
 * tier ごとの削除条件テキスト (純粋). 数値は必ず `policy` (server 供給) から
 * 埋め込む (web に 7/30 を直書きしない).
 */
export const retentionLadderDetail = (
  tier: RetentionTier,
  policy: RoomRetention['policy'],
): string => {
  switch (tier) {
    case 'anonymous':
      return `${policy.anonymousDays}日アクセスがないと削除`;
    case 'password':
      return `${policy.passwordDays}日アクセスがないと削除`;
    case 'email':
      return `${policy.emailWarnDays}日で警告メール → さらに${policy.emailGraceDays}日後に削除`;
  }
};

const RoomRetentionInfo = ({
  roomId,
  adminToken,
  loadRetention,
}: {
  readonly roomId: string;
  readonly adminToken: string;
  readonly loadRetention?: (input: FetchRoomRetentionInput) => Promise<FetchRoomRetentionOutcome>;
}): React.ReactNode => {
  const [retention, setRetention] = useState<RoomRetention | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    const load = loadRetention ?? ((input: FetchRoomRetentionInput) => fetchRoomRetention(input));
    void (async () => {
      const out = await load({
        dispatcherUrl: readEnv().dispatcherUrl,
        roomId,
        adminToken,
        signal: ac.signal,
      });
      if (ac.signal.aborted) return;
      // 取得失敗 (unauthorized / not-found / error) は黙って説明を出さない.
      setRetention(out.outcome === 'ok' ? out.retention : null);
    })();
    return () => {
      ac.abort();
    };
  }, [roomId, adminToken, loadRetention]);

  // 取得前 / 取得失敗は非表示 (設定操作自体は他フィールドで従来通り可能).
  if (retention === null) return null;

  const { policy, tier } = retention;

  return (
    <section
      aria-label="この部屋の保存期間"
      className="rounded border border-white/10 bg-[var(--color-bg-soft)] px-3 py-2"
    >
      <h3 className="text-sm font-bold text-[var(--color-text)]">この部屋の保存期間</h3>
      <p className="mt-1 text-xs text-[var(--color-text-muted)]">
        最後にアクセスがあってから一定期間で自動的に削除されます。
        <br />
        誰かが入室すると期限は延びます（未使用の期間だけがカウントされます）。
      </p>
      <ul className="mt-2 flex flex-col gap-1">
        {RETENTION_LADDER.map((row) => {
          const current = row.tier === tier;
          return (
            <li
              key={row.tier}
              aria-current={current ? 'true' : undefined}
              className={`flex flex-wrap items-baseline gap-x-2 rounded px-2 py-1 text-xs ${
                current
                  ? 'bg-[var(--color-accent)]/15 text-[var(--color-text)]'
                  : 'text-[var(--color-text-muted)]'
              }`}
            >
              <span className={`font-bold ${current ? 'text-[var(--color-accent)]' : ''}`}>
                {row.title}
              </span>
              {current && (
                <span className="rounded bg-[var(--color-accent)] px-1.5 py-0.5 text-[10px] font-bold text-[var(--color-panel)]">
                  現在
                </span>
              )}
              <span>{retentionLadderDetail(row.tier, policy)}</span>
            </li>
          );
        })}
      </ul>
      {tier !== 'email' && (
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">
          下の項目でパスワードやメールを登録すると、この部屋を長く残せます。
        </p>
      )}
    </section>
  );
};

/* ─── 1. ルーム名 ────────────────────────────────────────────────────────── */

const NameField = ({
  roomId,
  initial,
  dispatcher,
  auth,
}: {
  readonly roomId: string;
  readonly initial: string;
  readonly dispatcher: DispatcherClient;
  readonly auth: AdminAuthOptions;
}): React.ReactNode => {
  const [value, setValue] = useState(initial);
  const [fieldErrorMessage, setFieldErrorMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<FieldSaveResult | null>(null);

  const onSave = async (): Promise<void> => {
    if (saving) return;
    setResult(null);
    const v = validateRoomName(value);
    if (v.outcome !== 'ok') {
      setFieldErrorMessage(roomNameErrorText(v.error));
      return;
    }
    setFieldErrorMessage(null);
    setSaving(true);
    const out = await dispatcher.patchRoom(roomId, { name: v.value }, auth);
    setSaving(false);
    const mapped = mapPatchRoomOutcome(out);
    if (mapped.outcome === 'ok') {
      // Show "保存しました" briefly.
      setResult({ outcome: 'ok', message: 'ルーム名を保存しました。' });
    } else {
      setResult(mapped);
    }
  };

  return (
    <div>
      <label className="block text-sm">
        ルーム名
        <input
          type="text"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (fieldErrorMessage !== null) setFieldErrorMessage(null);
            if (result !== null) setResult(null);
          }}
          maxLength={ROOM_NAME_MAX_LENGTH}
          disabled={saving}
          className="mt-1 w-full rounded border border-white/30 bg-[var(--color-bg-soft)] px-2 py-1 text-base text-white outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
        />
      </label>
      {fieldErrorMessage !== null && (
        <p className="mt-1 text-xs text-[var(--color-error)]" role="alert">
          {fieldErrorMessage}
        </p>
      )}
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={() => {
            void onSave();
          }}
          disabled={saving || value.trim() === initial.trim()}
          className="rounded bg-[var(--color-accent)] px-3 py-1 text-sm font-bold text-[var(--color-panel)] transition hover:brightness-110 disabled:opacity-50"
        >
          {saving ? '保存中…' : '保存'}
        </button>
      </div>
      <FieldFeedback result={result} />
    </div>
  );
};

/* ─── 2. オーナーパスワード ─────────────────────────────────────────────── */

const PasswordField = ({
  roomId,
  dispatcher,
  auth,
}: {
  readonly roomId: string;
  readonly dispatcher: DispatcherClient;
  readonly auth: AdminAuthOptions;
}): React.ReactNode => {
  const [value, setValue] = useState('');
  const [fieldErrorMessage, setFieldErrorMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<FieldSaveResult | null>(null);
  const [savedAtLeastOnce, setSavedAtLeastOnce] = useState(false);

  const onSave = async (): Promise<void> => {
    if (saving) return;
    setResult(null);
    const v = validateOwnerPassword(value);
    if (v.outcome !== 'ok') {
      setFieldErrorMessage(ownerPasswordErrorText(v.error));
      return;
    }
    setFieldErrorMessage(null);
    setSaving(true);
    const out = await dispatcher.setOwnerPassword(roomId, v.value, auth);
    setSaving(false);
    const mapped = mapSetOwnerPasswordOutcome(out);
    if (mapped.outcome === 'ok') {
      setSavedAtLeastOnce(true);
      setValue('');
    }
    setResult(mapped);
  };

  return (
    <div>
      <p className="block text-sm">
        オーナーパスワード
        {savedAtLeastOnce && (
          <span className="ml-2 text-xs text-[var(--color-success)]">設定済</span>
        )}
      </p>
      <p className="mb-1 text-xs text-[var(--color-text-muted)]">
        別端末からこのルームをオーナーとして開くために使えます。
      </p>
      <input
        type="password"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          if (fieldErrorMessage !== null) setFieldErrorMessage(null);
          if (result !== null) setResult(null);
        }}
        minLength={OWNER_PASSWORD_MIN_LENGTH}
        maxLength={OWNER_PASSWORD_MAX_LENGTH}
        disabled={saving}
        autoComplete="new-password"
        className="w-full rounded border border-white/30 bg-[var(--color-bg-soft)] px-2 py-1 text-base text-white outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
        placeholder={`${OWNER_PASSWORD_MIN_LENGTH}〜${OWNER_PASSWORD_MAX_LENGTH} 文字`}
      />
      {fieldErrorMessage !== null && (
        <p className="mt-1 text-xs text-[var(--color-error)]" role="alert">
          {fieldErrorMessage}
        </p>
      )}
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={() => {
            void onSave();
          }}
          disabled={saving || value.length === 0}
          className="rounded bg-[var(--color-accent)] px-3 py-1 text-sm font-bold text-[var(--color-panel)] transition hover:brightness-110 disabled:opacity-50"
        >
          {saving ? '保存中…' : '保存'}
        </button>
      </div>
      <FieldFeedback result={result} />
    </div>
  );
};

/* ─── 2.5. 入室あいことば (owner-vs-entry-password-2026-06-13.md) ─────────
 *
 * オーナーパスワード (上のセクション) とは **別セクション** に置く。
 * 見出し・説明文で「オーナーパスワード = あなた (オーナー) 用」「あいことば =
 * 入室するゲスト用、あなたは打たなくて入れる」を区別する (混同防止の確定方針、
 * 主策は「オーナーは打たない」= 使う人で別物化)。見出しの「（パスワード）」は
 * 2026-09-19 追記 (結論 1 の部分改定、作者判断) — 旧版ユーザー向けの理解補助。
 *
 * PATCH /admin/rooms/:id { entryPassword } の string=設定/変更、null=解除の
 * 2 通り。「解除する」ボタンは現在設定済 (`required`) のときだけ出す。
 * -------------------------------------------------------------------------- */

const EntryPasswordField = ({
  roomId,
  dispatcher,
  auth,
  current,
}: {
  readonly roomId: string;
  readonly dispatcher: DispatcherClient;
  readonly auth: AdminAuthOptions;
  /** lookup 解決前は `null` (= 「設定済」バッジをまだ出さない). */
  readonly current: boolean | null;
}): React.ReactNode => {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<FieldSaveResult | null>(null);
  // PATCH 応答を信頼して表示状態を更新する (ClearOpenToggle と同じ流儀).
  const [required, setRequired] = useState<boolean>(current ?? false);
  // lookup が (mount 後の非同期取得で) 解決/変化したら追従する.
  useEffect(() => {
    if (current !== null) setRequired(current);
  }, [current]);

  // 正規化後 (NFKC + trim) の値で空欄判定・送信する — 全角スペースだけの
  // 入力を「設定する」で送ると server が 400 で弾くため
  // (normalizeEntryPassword のコメント参照).
  const normalizedValue = normalizeEntryPassword(value);

  const onSave = async (): Promise<void> => {
    if (saving || normalizedValue.length === 0) return;
    setResult(null);
    setSaving(true);
    const out = await dispatcher.patchRoom(roomId, { entryPassword: normalizedValue }, auth);
    setSaving(false);
    const mapped = mapPatchRoomOutcome(out);
    if (mapped.outcome === 'ok') {
      if (out.outcome === 'ok') setRequired(out.entryPasswordRequired);
      setValue('');
      setResult({ outcome: 'ok', message: 'あいことばを設定しました。' });
      return;
    }
    setResult(mapped);
  };

  const onClear = async (): Promise<void> => {
    if (saving) return;
    setResult(null);
    setSaving(true);
    const out = await dispatcher.patchRoom(roomId, { entryPassword: null }, auth);
    setSaving(false);
    const mapped = mapPatchRoomOutcome(out);
    if (mapped.outcome === 'ok') {
      if (out.outcome === 'ok') setRequired(out.entryPasswordRequired);
      setValue('');
      setResult({ outcome: 'ok', message: 'あいことばを解除しました。' });
      return;
    }
    setResult(mapped);
  };

  return (
    <div>
      <p className="block text-sm">
        あいことば（パスワード）
        {required && <span className="ml-2 text-xs text-[var(--color-success)]">設定済</span>}
      </p>
      <p className="mb-1 text-xs text-[var(--color-text-muted)]">
        入室するゲストが打つ合言葉です。あなた (オーナー) は打たずに入れます。
      </p>
      <input
        type="text"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          if (result !== null) setResult(null);
        }}
        maxLength={MAX_ENTRY_PASSWORD_LENGTH}
        disabled={saving}
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        className="w-full rounded border border-white/30 bg-[var(--color-bg-soft)] px-2 py-1 text-base text-white outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
        placeholder={`1〜${MAX_ENTRY_PASSWORD_LENGTH} 文字`}
      />
      <div className="mt-2 flex justify-end gap-2">
        {required && (
          <button
            type="button"
            onClick={() => {
              void onClear();
            }}
            disabled={saving}
            className="rounded border border-white/30 bg-[var(--color-bg-soft)] px-3 py-1 text-sm text-white transition hover:bg-[var(--color-bg)] disabled:opacity-50"
          >
            解除する
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            void onSave();
          }}
          disabled={saving || normalizedValue.length === 0}
          className="rounded bg-[var(--color-accent)] px-3 py-1 text-sm font-bold text-[var(--color-panel)] transition hover:brightness-110 disabled:opacity-50"
        >
          {saving ? '保存中…' : '設定する'}
        </button>
      </div>
      <FieldFeedback result={result} />
    </div>
  );
};

/* ─── 3. email 登録 ───────────────────────────────────────────────────────── */

const EmailField = ({
  roomId,
  dispatcher,
  auth,
}: {
  readonly roomId: string;
  readonly dispatcher: DispatcherClient;
  readonly auth: AdminAuthOptions;
}): React.ReactNode => {
  const [value, setValue] = useState('');
  const [fieldErrorMessage, setFieldErrorMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<FieldSaveResult | null>(null);

  const onSave = async (): Promise<void> => {
    if (saving) return;
    setResult(null);
    const v = validateOwnerEmail(value);
    if (v.outcome !== 'ok') {
      setFieldErrorMessage(ownerEmailErrorText(v.error));
      return;
    }
    setFieldErrorMessage(null);
    setSaving(true);
    const out = await dispatcher.setOwnerEmail(roomId, v.value, auth);
    setSaving(false);
    setResult(mapSetOwnerEmailOutcome(out));
  };

  return (
    <div>
      <p className="block text-sm">メールアドレス登録</p>
      <p className="mb-1 text-xs text-[var(--color-text-muted)]">
        このメール宛に確認メールが届きます。リンクを踏むと、次回以降このメールアドレスで再ログインできるようになります。
      </p>
      <input
        type="email"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          if (fieldErrorMessage !== null) setFieldErrorMessage(null);
          if (result !== null) setResult(null);
        }}
        maxLength={OWNER_EMAIL_MAX_LENGTH}
        disabled={saving}
        autoComplete="email"
        spellCheck={false}
        className="w-full rounded border border-white/30 bg-[var(--color-bg-soft)] px-2 py-1 text-base text-white outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
        placeholder="owner@example.com"
      />
      {fieldErrorMessage !== null && (
        <p className="mt-1 text-xs text-[var(--color-error)]" role="alert">
          {fieldErrorMessage}
        </p>
      )}
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={() => {
            void onSave();
          }}
          disabled={saving || value.length === 0}
          className="rounded bg-[var(--color-accent)] px-3 py-1 text-sm font-bold text-[var(--color-panel)] transition hover:brightness-110 disabled:opacity-50"
        >
          {saving ? '保存中…' : '保存'}
        </button>
      </div>
      <FieldFeedback result={result} />
    </div>
  );
};

/* ─── 4. 公開フラグ (CB toggle で即保存) ─────────────────────────────────── */
// 2026-05-23: W2 公開時点では非表示化 (= Phase 14+ HomePage list 機能投入時に
// 復活させる). 実装は丸ごと残してあるので、JSX 側のコメントアウトを外し、
// `_PublicToggle` を `PublicToggle` に rename し直すだけで復活可能.

const _PublicToggle = ({
  roomId,
  initial,
  dispatcher,
  auth,
}: {
  readonly roomId: string;
  readonly initial: boolean;
  readonly dispatcher: DispatcherClient;
  readonly auth: AdminAuthOptions;
}): React.ReactNode => {
  const [checked, setChecked] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<FieldSaveResult | null>(null);

  const onToggle = async (next: boolean): Promise<void> => {
    if (saving) return;
    setChecked(next); // optimistic
    setResult(null);
    setSaving(true);
    const out = await dispatcher.patchRoom(roomId, { isPublic: next }, auth);
    setSaving(false);
    const mapped = mapPatchRoomOutcome(out);
    if (mapped.outcome === 'ok') {
      // PATCH response has the canonical isPublic; trust it (would only differ
      // under racing PATCH calls, which we don't issue concurrently).
      if (out.outcome === 'ok') setChecked(out.isPublic);
      setResult({ outcome: 'ok', message: '公開設定を保存しました。' });
      return;
    }
    // Roll back optimistic toggle on failure.
    setChecked(!next);
    setResult(mapped);
  };

  return (
    <div>
      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => {
            void onToggle(e.target.checked);
          }}
          disabled={saving}
        />
        ルームを公開する (一覧に掲載 — Phase 14+ で利用予定)
      </label>
      <FieldFeedback result={result} />
    </div>
  );
};

/* ─── 5. clearOpen toggle (Phase 7 Task 7-5、CB toggle で即保存) ─────────── */

const ClearOpenToggle = ({
  roomId,
  initial,
  dispatcher,
  auth,
}: {
  readonly roomId: string;
  readonly initial: boolean;
  readonly dispatcher: DispatcherClient;
  readonly auth: AdminAuthOptions;
}): React.ReactNode => {
  const [checked, setChecked] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<FieldSaveResult | null>(null);

  const onToggle = async (next: boolean): Promise<void> => {
    if (saving) return;
    setChecked(next); // optimistic
    setResult(null);
    setSaving(true);
    const out = await dispatcher.patchRoom(roomId, { clearOpen: next }, auth);
    setSaving(false);
    const mapped = mapPatchRoomOutcome(out);
    if (mapped.outcome === 'ok') {
      // PATCH response の clearOpen を信頼 (PublicToggle と同じ流儀).
      if (out.outcome === 'ok') setChecked(out.clearOpen);
      setResult({ outcome: 'ok', message: 'クリア権限を保存しました。' });
      return;
    }
    // Roll back optimistic toggle on failure.
    setChecked(!next);
    setResult(mapped);
  };

  return (
    <div>
      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => {
            void onToggle(e.target.checked);
          }}
          disabled={saving}
        />
        全員に「キャンバスのクリア」を許可する
      </label>
      <p className="mt-1 text-xs text-[var(--color-text-muted)]">
        オフにすると、CLEAR 権限を持つ認証アカウントのみがキャンバスをクリアできます。
        <br />
        変更は次回の入室から反映されます。
      </p>
      <FieldFeedback result={result} />
    </div>
  );
};

/* ─── 5.5. 入室画面の表示トグル (entry-preview-members-2026-07-21) ────────── */
//
// 「入室画面にキャンバスを表示」「入室画面に参加者を表示」の 2 個を 1 つの
// パラメタライズド CB toggle で賄う (ClearOpenToggle と同じ optimistic +
// PATCH 応答 trust + 失敗 rollback の流儀). 文言・見た目は共有 spec + 共有 CB
// (entryVisibilityFields / EntryVisibilityCheckbox) 由来で CreateRoomPage と
// 同一知識 (案 A). 初期値 (現在値) の解決は親 (RoomSettingsModal) の責務.

const EntryVisibilityToggle = ({
  roomId,
  spec,
  initial,
  dispatcher,
  auth,
}: {
  readonly roomId: string;
  readonly spec: EntryVisibilityFieldSpec;
  readonly initial: boolean;
  readonly dispatcher: DispatcherClient;
  readonly auth: AdminAuthOptions;
}): React.ReactNode => {
  const [checked, setChecked] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<FieldSaveResult | null>(null);

  const onToggle = async (next: boolean): Promise<void> => {
    if (saving) return;
    setChecked(next); // optimistic
    setResult(null);
    setSaving(true);
    const body =
      spec.key === 'entryShowPreview' ? { entryShowPreview: next } : { entryShowMembers: next };
    const out = await dispatcher.patchRoom(roomId, body, auth);
    setSaving(false);
    const mapped = mapPatchRoomOutcome(out);
    if (mapped.outcome === 'ok') {
      // PATCH response の値を信頼 (ClearOpenToggle と同じ流儀).
      if (out.outcome === 'ok') {
        setChecked(spec.key === 'entryShowPreview' ? out.entryShowPreview : out.entryShowMembers);
      }
      setResult({ outcome: 'ok', message: '入室画面の表示設定を保存しました。' });
      return;
    }
    // Roll back optimistic toggle on failure.
    setChecked(!next);
    setResult(mapped);
  };

  return (
    <div>
      <EntryVisibilityCheckbox
        spec={spec}
        checked={checked}
        disabled={saving}
        onChange={(next) => {
          void onToggle(next);
        }}
      />
      <FieldFeedback result={result} />
    </div>
  );
};

/* ─── 6. canvas size 変更不可の注記 ─────────────────────────────────────── */

const CanvasSizeNotice = (): React.ReactNode => (
  <p className="text-xs text-[var(--color-text-muted)]">
    キャンバスサイズの変更は今後のアップデートで対応予定です。
  </p>
);

/* ─── 7. Phase 6 Task 8: 認証アカウント (credential) 管理 ───────────────── */

interface CredentialsPanelProps {
  readonly roomId: string;
  readonly dispatcher: DispatcherClient;
  /** Bearer or cookie session 両対応. Bearer 無しなら cookie 経由. */
  readonly auth: RoomAdminAuthOptions;
}

type CredListState =
  | { readonly t: 'loading' }
  | { readonly t: 'ok'; readonly credentials: readonly CredentialSummary[] }
  | { readonly t: 'error'; readonly message: string };

// 2026-05-23: 非表示化のため未使用. Phase 14+ 復活時に `_` prefix 除去.
const _CredentialsPanel = ({
  roomId,
  dispatcher,
  auth,
}: CredentialsPanelProps): React.ReactNode => {
  const [state, setState] = useState<CredListState>({ t: 'loading' });
  const [actionMessage, setActionMessage] = useState<{
    readonly tone: 'success' | 'error';
    readonly text: string;
  } | null>(null);
  // 2026-05-21: 削除確認モーダル. 旧 `window.confirm` 置換 (iPad Safari 全画面
  // 解除対策). ネスト modal なので ConfirmDialog 側で `z-[60]` を持ち RoomSettings
  // Modal (`z-50`) より上に出る. target を memorize しておき、OK で API 実行.
  const [deleteTarget, setDeleteTarget] = useState<CredentialSummary | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setState({ t: 'loading' });
    const out = await dispatcher.listCredentials(roomId, auth);
    const mapped = mapListCredentialsOutcome(out);
    if (shouldNavigateToLogin(mapped.outcome)) {
      goToLoginScreen();
      return;
    }
    if (mapped.outcome === 'ok') {
      setState({ t: 'ok', credentials: mapped.credentials });
      return;
    }
    setState({ t: 'error', message: mapped.message ?? 'エラーが発生しました。' });
  }, [dispatcher, roomId, auth]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onCreated = useCallback((cred: CredentialSummary): void => {
    // Append to list.
    setState((prev) =>
      prev.t === 'ok' ? { t: 'ok', credentials: [...prev.credentials, cred] } : prev,
    );
    setActionMessage({ tone: 'success', text: `「${cred.loginId}」を作成しました。` });
  }, []);

  // 削除ボタン click: state set のみ. 実際の API call は ConfirmDialog の
  // onConfirm 経路 (executeDelete) で行う.
  const onDelete = (cred: CredentialSummary): void => {
    setDeleteTarget(cred);
  };

  // ConfirmDialog OK 後の実処理. 旧 `onDelete` の confirm 以降の部分を切り出す.
  const executeDelete = async (cred: CredentialSummary): Promise<void> => {
    setDeleteTarget(null);
    setActionMessage(null);
    const out = await dispatcher.deleteCredential(roomId, cred.id, auth);
    const mapped = mapDeleteCredentialOutcome(out);
    if (shouldNavigateToLogin(mapped.outcome)) {
      goToLoginScreen();
      return;
    }
    if (mapped.outcome === 'ok') {
      setState((prev) =>
        prev.t === 'ok'
          ? { t: 'ok', credentials: prev.credentials.filter((c) => c.id !== cred.id) }
          : prev,
      );
      setActionMessage({ tone: 'success', text: `「${cred.loginId}」を削除しました。` });
      return;
    }
    setActionMessage({
      tone: 'error',
      text: mapped.message ?? '削除に失敗しました。',
    });
  };

  return (
    <section aria-label="認証アカウント管理">
      <h3 className="mb-2 text-sm font-bold text-[var(--color-text)]">
        認証アカウント (credential)
      </h3>
      <p className="mb-2 text-xs text-[var(--color-text-muted)]">
        ログイン ID + パスワードで入室できる認証アカウントを発行します。KICK / IMAGEPASTE / CLEAR の
        権限を CB で個別に付与できます。
      </p>

      {state.t === 'loading' && (
        <p className="text-xs text-[var(--color-text-muted)]" role="status">
          読み込み中…
        </p>
      )}
      {state.t === 'error' && (
        <div className="mb-2 rounded border border-[var(--color-error)]/40 bg-[var(--color-bg-soft)] px-2 py-2">
          <p className="text-xs text-[var(--color-error)]" role="alert">
            {state.message}
          </p>
          <button
            type="button"
            onClick={() => {
              void refresh();
            }}
            className="mt-1 rounded border border-white/30 bg-[var(--color-bg)] px-2 py-0.5 text-xs text-white hover:bg-[var(--color-bg-soft)]"
          >
            再試行
          </button>
        </div>
      )}
      {state.t === 'ok' && state.credentials.length === 0 && (
        <p className="mb-2 text-xs text-[var(--color-text-muted)]">
          登録済の認証アカウントはありません。
        </p>
      )}
      {state.t === 'ok' && state.credentials.length > 0 && (
        <ul className="mb-3 flex flex-col gap-1">
          {state.credentials.map((c) => (
            <CredentialRow
              key={c.id}
              cred={c}
              onDelete={() => {
                onDelete(c);
              }}
            />
          ))}
        </ul>
      )}

      <CredentialCreateForm
        roomId={roomId}
        dispatcher={dispatcher}
        auth={auth}
        onCreated={onCreated}
        onMessage={setActionMessage}
      />

      {actionMessage !== null && (
        <p
          className={`mt-2 text-xs ${
            actionMessage.tone === 'success'
              ? 'text-[var(--color-success)]'
              : 'text-[var(--color-error)]'
          }`}
          role={actionMessage.tone === 'error' ? 'alert' : 'status'}
        >
          {actionMessage.text}
        </p>
      )}

      {/* 2026-05-21: 認証アカウント削除確認モーダル. 旧 `window.confirm` 置換
          (iPad Safari 全画面解除対策). RoomSettingsModal (`z-50`) の中に乗せる
          ネスト modal なので ConfirmDialog 側 `z-[60]` で上層に出る. */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="認証アカウントを削除"
        message={
          deleteTarget !== null
            ? `認証アカウント「${deleteTarget.loginId}」を削除しますか?\nこのアカウントでのログインができなくなります。`
            : ''
        }
        confirmLabel="削除"
        destructive
        onConfirm={() => {
          if (deleteTarget !== null) {
            void executeDelete(deleteTarget);
          }
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </section>
  );
};

const CredentialRow = ({
  cred,
  onDelete,
}: {
  readonly cred: CredentialSummary;
  readonly onDelete: () => void;
}): React.ReactNode => {
  const { kick, imagepaste, clear } = permissionsBooleansFromBitflag(cred.permissions);
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 rounded border border-white/10 bg-[var(--color-bg-soft)] px-2 py-1.5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm">
          <span className="font-mono">{cred.loginId}</span>
          {cred.displayName !== null && (
            <span className="ml-2 text-[var(--color-text-muted)]">({cred.displayName})</span>
          )}
        </p>
        <div className="mt-0.5 flex flex-wrap gap-1">
          {kick && (
            <span className="rounded bg-[var(--color-accent)]/30 px-1.5 py-0.5 text-xs text-[var(--color-accent)]">
              KICK
            </span>
          )}
          {imagepaste && (
            <span className="rounded bg-[var(--color-accent-soft)]/30 px-1.5 py-0.5 text-xs text-[var(--color-accent-soft)]">
              IMAGEPASTE
            </span>
          )}
          {clear && (
            <span className="rounded bg-[var(--color-accent)]/30 px-1.5 py-0.5 text-xs text-[var(--color-accent)]">
              CLEAR
            </span>
          )}
          {!kick && !imagepaste && !clear && (
            <span className="text-xs text-[var(--color-text-muted)]">権限なし</span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onDelete}
        aria-label={`${cred.loginId} を削除`}
        className="flex-shrink-0 rounded bg-[var(--color-error)] px-2 py-1 text-xs font-bold text-white transition hover:brightness-110"
      >
        削除
      </button>
    </li>
  );
};

const CredentialCreateForm = ({
  roomId,
  dispatcher,
  auth,
  onCreated,
  onMessage,
}: {
  readonly roomId: string;
  readonly dispatcher: DispatcherClient;
  readonly auth: RoomAdminAuthOptions;
  readonly onCreated: (cred: CredentialSummary) => void;
  readonly onMessage: (m: { tone: 'success' | 'error'; text: string } | null) => void;
}): React.ReactNode => {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [kick, setKick] = useState(false);
  const [imagepaste, setImagepaste] = useState(false);
  // Phase 7 Task 7-5: CLEAR 権限の付与 CB. clearOpen=true のルームでは付与不要だが、
  // 「将来 clearOpen を OFF にするかも」のために発行時に付けておけるようにする.
  const [clear, setClear] = useState(false);
  const [errors, setErrors] = useState<{
    loginId?: string;
    password?: string;
    displayName?: string;
    permissions?: string;
  }>({});
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    onMessage(null);
    const v = validateCredentialForm({
      loginId,
      password,
      displayName,
      permissions: permissionsBitflagFromBooleans({ kick, imagepaste, clear }),
    });
    if (v.outcome !== 'ok') {
      setErrors({
        loginId:
          v.errors.loginId !== undefined ? credentialLoginIdErrorText(v.errors.loginId) : undefined,
        password:
          v.errors.password !== undefined
            ? credentialPasswordErrorText(v.errors.password)
            : undefined,
        displayName:
          v.errors.displayName !== undefined
            ? credentialDisplayNameErrorText(v.errors.displayName)
            : undefined,
        permissions:
          v.errors.permissions !== undefined
            ? credentialPermissionsErrorText(v.errors.permissions)
            : undefined,
      });
      return;
    }
    setErrors({});
    setSubmitting(true);
    const out = await dispatcher.createCredential(roomId, v.body, auth);
    setSubmitting(false);
    const mapped = mapCreateCredentialOutcome(out);
    if (shouldNavigateToLogin(mapped.outcome)) {
      goToLoginScreen();
      return;
    }
    if (mapped.outcome === 'ok' && out.outcome === 'ok') {
      onCreated(out.credential);
      // reset form
      setLoginId('');
      setPassword('');
      setDisplayName('');
      setKick(false);
      setImagepaste(false);
      setClear(false);
      return;
    }
    onMessage({
      tone: 'error',
      text: mapped.message ?? '認証アカウントの作成に失敗しました。',
    });
  };

  return (
    <form
      onSubmit={(e) => {
        void onSubmit(e);
      }}
      aria-label="認証アカウントを発行"
      className="rounded border border-white/10 bg-[var(--color-bg-soft)] p-2"
    >
      <p className="mb-2 text-sm font-bold text-[var(--color-text)]">新規アカウント発行</p>
      <label className="mb-2 block text-sm">
        ログイン ID
        <input
          type="text"
          value={loginId}
          onChange={(e) => {
            setLoginId(e.target.value);
            if (errors.loginId !== undefined) setErrors({ ...errors, loginId: undefined });
          }}
          maxLength={CREDENTIAL_LOGIN_ID_MAX_LENGTH}
          disabled={submitting}
          autoComplete="off"
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          className="mt-1 w-full rounded border border-white/30 bg-[var(--color-bg)] px-2 py-1 font-mono text-sm text-white outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
          placeholder="alice"
        />
        {errors.loginId !== undefined && (
          <span className="mt-0.5 block text-xs text-[var(--color-error)]">{errors.loginId}</span>
        )}
      </label>

      <label className="mb-2 block text-sm">
        パスワード
        <input
          type="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (errors.password !== undefined) setErrors({ ...errors, password: undefined });
          }}
          minLength={CREDENTIAL_PASSWORD_MIN_LENGTH}
          maxLength={CREDENTIAL_PASSWORD_MAX_LENGTH}
          disabled={submitting}
          autoComplete="new-password"
          className="mt-1 w-full rounded border border-white/30 bg-[var(--color-bg)] px-2 py-1 text-sm text-white outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
          placeholder={`${CREDENTIAL_PASSWORD_MIN_LENGTH}〜${CREDENTIAL_PASSWORD_MAX_LENGTH} 文字`}
        />
        {errors.password !== undefined && (
          <span className="mt-0.5 block text-xs text-[var(--color-error)]">{errors.password}</span>
        )}
      </label>

      <label className="mb-2 block text-sm">
        表示名 (任意)
        <input
          type="text"
          value={displayName}
          onChange={(e) => {
            setDisplayName(e.target.value);
            if (errors.displayName !== undefined) setErrors({ ...errors, displayName: undefined });
          }}
          maxLength={CREDENTIAL_DISPLAY_NAME_MAX_LENGTH}
          disabled={submitting}
          className="mt-1 w-full rounded border border-white/30 bg-[var(--color-bg)] px-2 py-1 text-sm text-white outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
          placeholder="未指定なら入室時 displayName が使われます"
        />
        {errors.displayName !== undefined && (
          <span className="mt-0.5 block text-xs text-[var(--color-error)]">
            {errors.displayName}
          </span>
        )}
      </label>

      <fieldset className="mb-2">
        <legend className="text-sm">権限</legend>
        <label className="mr-3 inline-flex items-center gap-1 text-sm">
          <input
            type="checkbox"
            checked={kick}
            onChange={(e) => setKick(e.target.checked)}
            disabled={submitting}
          />
          KICK
        </label>
        <label className="mr-3 inline-flex items-center gap-1 text-sm">
          <input
            type="checkbox"
            checked={imagepaste}
            onChange={(e) => setImagepaste(e.target.checked)}
            disabled={submitting}
          />
          IMAGEPASTE
        </label>
        <label className="inline-flex items-center gap-1 text-sm">
          <input
            type="checkbox"
            checked={clear}
            onChange={(e) => setClear(e.target.checked)}
            disabled={submitting}
          />
          CLEAR
        </label>
        {errors.permissions !== undefined && (
          <span className="mt-0.5 block text-xs text-[var(--color-error)]">
            {errors.permissions}
          </span>
        )}
      </fieldset>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={submitting || loginId.length === 0 || password.length === 0}
          className="rounded bg-[var(--color-accent)] px-3 py-1 text-sm font-bold text-[var(--color-panel)] transition hover:brightness-110 disabled:opacity-50"
        >
          {submitting ? '発行中…' : '発行'}
        </button>
      </div>
    </form>
  );
};
